import { useMemo } from "react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "@mui/stylis-plugin-rtl";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { resolveBaseLanguage } from "../i18n";

const cacheRtl = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin] });

/** Overrides for MUI form fields in RTL (Urdu). Shell layout keeps theme.direction ltr. */
const formFieldComponents = {
  MuiInputBase: {
    styleOverrides: {
      input: { textAlign: "start" },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: { textAlign: "start" },
    },
  },
  MuiFilledInput: {
    styleOverrides: {
      input: { textAlign: "start" },
    },
  },
  MuiSelect: {
    styleOverrides: {
      select: { textAlign: "start" },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: { textAlign: "start" },
    },
  },
};

/**
 * Wraps MUI form content so OutlinedInput labels, placeholders, and Select text follow RTL
 * when the UI language is Urdu, without setting the global MUI theme to rtl (which would
 * mirror the app shell, sidebar, and drawers).
 */
export default function FormDirectionScope({ children }) {
  const outerTheme = useTheme();
  const { i18n } = useTranslation();
  const language = resolveBaseLanguage(i18n.resolvedLanguage || i18n.language || "en");

  const rtlFormTheme = useMemo(
    () => createTheme(outerTheme, { direction: "rtl", components: formFieldComponents }),
    [outerTheme],
  );

  if (language !== "ur") {
    return children;
  }

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={rtlFormTheme}>{children}</ThemeProvider>
    </CacheProvider>
  );
}
